import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Download, CheckCircle2, ArrowRight, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { ApiSection } from "@/components/import/ApiSection";
import { EmailSection } from "@/components/import/EmailSection";
import { PlanStep } from "@/components/import/PlanStep";

const STEPS = ["Origine", "Mappatura", "Report", "Pianificazione"];
const SKIP = "__skip__";
const RESULT_CLS = { nuovo: "bg-emerald-50 text-emerald-800", aggiornato: "bg-sky-50 text-sky-800", duplicato: "bg-amber-50 text-amber-800", scartato: "bg-red-50 text-red-700" };

const SAMPLES = {
  DELIVERY: "Codice Ordine;Cliente;Telefono;Comune;Provincia;Indirizzo;Civico;Piano;Tipo attivazione;Tecnologia;Codice ordine OLO;ROE;Porta splitter;PTO;Data appuntamento\nWR900101;Rossi Mario;3471234567;Milano;MI;Via Torino;12;2;Nuova linea;FTTH;NR551201;ROE-410;P3/S1;PTO7781;\nWR900102;Bianchi Sara;3489876543;Monza;MB;Via Italia;5;0;Migrazione;FTTH;NR551202;ROE-411;P5/S2;PTO7782;\nWR900103;;3401112223;;MI;Via Dante;8;1;Trasloco;FTTC;NR551203;;;;\n",
  ASSURANCE: "Ticket;Cliente;Telefono;Comune;Provincia;Indirizzo;Civico;Priorità;Sintomo;Data apertura\nTT500201;Verdi Luca;3335556667;Torino;TO;Corso Francia;100;urgente;Linea assente;\nTT500202;Neri Anna;3337778889;Moncalieri;TO;Via Roma;3;ordinaria;Degrado prestazioni;\n",
};

const Stepper = ({ step }) => (
  <div className="mb-8 flex border bg-white" data-testid="import-stepper">
    {STEPS.map((s, i) => (
      <div key={s} className={`flex flex-1 items-center gap-3 border-r px-5 py-4 last:border-0 ${i === step ? "bg-[#0B0F14] text-white" : i < step ? "text-primary" : "text-muted-foreground"}`}>
        <span className="font-mono text-xs">0{i + 1}</span><span className="text-sm font-semibold">{s}</span>
        {i < step && <CheckCircle2 className="ml-auto h-4 w-4" />}
      </div>
    ))}
  </div>
);

const SourceStep = ({ type, setType, projectId, setProjectId, sourceId, setSourceId, onFile, busy }) => {
  const { projects, integration_sources } = useData();
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const available = projects.filter((p) => p.service_types.includes(type));
  const ready = projectId && sourceId;
  const download = () => {
    const blob = new Blob([SAMPLES[type]], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `esempio_${type}.csv`;
    a.click();
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <div className="space-y-5 border bg-white p-6">
        <div>
          <Label className="micro-label">Attività</Label>
          <Select value={type} onValueChange={(v) => { setType(v); setProjectId(""); }}>
            <SelectTrigger className="mt-1.5" data-testid="import-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="DELIVERY">Delivery (attivazioni)</SelectItem><SelectItem value="ASSURANCE">Assurance (guasti)</SelectItem><SelectItem value="CONSTRUCTION">Construction (lavori di rete)</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="micro-label">Commessa / committente</Label>
          <Select value={projectId || undefined} onValueChange={setProjectId}>
            <SelectTrigger className="mt-1.5" data-testid="import-project-select"><SelectValue placeholder="Seleziona commessa" /></SelectTrigger>
            <SelectContent>{available.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="micro-label">Sorgente / provenienza</Label>
          <Select value={sourceId || undefined} onValueChange={setSourceId}>
            <SelectTrigger className="mt-1.5" data-testid="import-source-select"><SelectValue placeholder="Seleziona sorgente" /></SelectTrigger>
            <SelectContent>{integration_sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.kind})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="w-full" onClick={download} data-testid="import-sample-download"><Download className="mr-1 h-4 w-4" />Scarica file di esempio</Button>
      </div>
      <button type="button" disabled={!ready || busy} onClick={() => ref.current?.click()} data-testid="import-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (ready && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
        className={`flex min-h-[280px] flex-col items-center justify-center border-2 border-dashed bg-white p-10 transition-colors ${drag ? "border-primary bg-accent" : "hover:border-primary"} disabled:cursor-not-allowed disabled:opacity-50`}>
        <UploadCloud className="h-10 w-10 text-primary" />
        <div className="mt-4 font-display text-xl font-bold">{busy ? "Lettura file…" : "Trascina qui il file Excel o CSV"}</div>
        <div className="mt-1 text-sm text-muted-foreground">{ready ? "oppure clicca per selezionarlo (.xlsx, .csv — max 10MB). Il file originale viene conservato riga per riga." : "Seleziona prima commessa e sorgente"}</div>
        <input ref={ref} type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" data-testid="import-file-input"
          onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ""; }} />
      </button>
    </div>
  );
};

const MappingStep = ({ preview, fields, type, mapping, setMapping, onBack, onCommit, busy }) => {
  const visible = fields.filter((f) => f.types.includes(type));
  const missing = visible.filter((f) => f.required && !mapping[f.key]);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border bg-white px-6 py-4">
        <div className="text-sm"><span className="font-mono font-semibold">{preview.filename}</span> · {preview.total} righe · {preview.headers.length} colonne · mappatura {preview.mapping_source}</div>
        {missing.length > 0 && <div className="text-sm text-red-600" data-testid="import-missing-required">Colonne obbligatorie mancanti: {missing.map((f) => f.label).join(", ")}</div>}
      </div>
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <div className="border bg-white" data-testid="import-mapping-table">
          <div className="grid grid-cols-2 border-b bg-muted/50 px-4 py-2 micro-label"><span>Campo ERP</span><span>Colonna file</span></div>
          {visible.map((f) => (
            <div key={f.key} className="grid grid-cols-2 items-center gap-2 border-b px-4 py-2 last:border-0">
              <span className="text-sm">{f.label}{f.required && <span className="text-urgent"> *</span>}</span>
              <Select value={mapping[f.key] || SKIP} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v === SKIP ? undefined : v })}>
                <SelectTrigger className="h-8" data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SKIP}>— Ignora —</SelectItem>
                  {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="min-w-0">
          <div className="micro-label mb-2">Anteprima dati (prime {preview.sample.length} righe)</div>
          <div className="overflow-x-auto border bg-white" data-testid="import-preview-table">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">{preview.headers.map((h) => <TableHead key={h} className="whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {preview.sample.map((r, i) => <TableRow key={i}>{preview.headers.map((h) => <TableCell key={h} className={`whitespace-nowrap text-xs ${!r[h] ? "bg-red-50/40" : ""}`}>{r[h]}</TableCell>)}</TableRow>)}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Indietro</Button>
        <Button onClick={onCommit} disabled={busy || missing.length > 0} data-testid="import-commit-button">{busy ? "Importazione…" : <>Conferma importazione <ArrowRight className="ml-1 h-4 w-4" /></>}</Button>
      </div>
    </div>
  );
};

const ReportStep = ({ batch, onRestart, onPlan }) => (
  <div className="space-y-6" data-testid="import-report">
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {Object.entries(batch.counts).map(([k, v]) => (
        <div key={k} className="border bg-white p-6" data-testid={`import-count-${k}`}>
          <div className="micro-label">{k === "nuovo" ? "Nuovi" : k === "aggiornato" ? "Aggiornati" : k === "duplicato" ? "Duplicati" : "Scartati"}</div>
          <div className="mt-4 font-mono text-4xl font-semibold">{v}</div>
        </div>
      ))}
    </div>
    <div className="overflow-x-auto border bg-white">
      <Table>
        <TableHeader><TableRow className="bg-muted/50"><TableHead>Riga</TableHead><TableHead>Codice</TableHead><TableHead>Esito</TableHead><TableHead>Dettaglio</TableHead></TableRow></TableHeader>
        <TableBody>
          {batch.rows.map((r) => (
            <TableRow key={r.row_no}>
              <TableCell className="font-mono text-xs">{r.row_no}</TableCell>
              <TableCell className="font-mono text-sm">{r.external_order_id || "—"}</TableCell>
              <TableCell><span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${RESULT_CLS[r.result]}`}>{r.result}</span></TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <div className="flex flex-wrap gap-3">
      {onPlan && batch.counts.nuovo + batch.counts.aggiornato > 0 && <Button onClick={onPlan} data-testid="import-plan-button"><ArrowRight className="mr-1 h-4 w-4" />Pianifica le pratiche importate</Button>}
      <Button variant="outline" onClick={onRestart} data-testid="import-restart-button"><RotateCcw className="mr-1 h-4 w-4" />Nuova importazione</Button>
    </div>
  </div>
);

const History = ({ items }) => {
  const { nameOf } = useData();
  if (!items.length) return null;
  return (
    <div className="mt-12">
      <h2 className="mb-4 font-display text-xl font-bold">Storico importazioni</h2>
      <div className="overflow-x-auto border bg-white" data-testid="import-history">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead>Data</TableHead><TableHead>File</TableHead><TableHead>Sorgente</TableHead><TableHead>Commessa</TableHead><TableHead>Righe</TableHead><TableHead>Nuovi / Agg. / Dup. / Scart.</TableHead><TableHead>Utente</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-mono text-xs">{fmtDateTime(b.created_at)}</TableCell>
                <TableCell className="text-sm">{b.filename}</TableCell>
                <TableCell className="text-sm">{nameOf("integration_sources", b.source_id)}</TableCell>
                <TableCell className="font-mono text-xs">{nameOf("projects", b.project_id, "code")}</TableCell>
                <TableCell className="font-mono">{b.total}</TableCell>
                <TableCell className="font-mono text-sm">{b.counts.nuovo} / {b.counts.aggiornato} / {b.counts.duplicato} / {b.counts.scartato}</TableCell>
                <TableCell className="text-sm">{b.created_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default function ImportPage() {
  const { can } = useAuth();
  const [step, setStep] = useState(0);
  const [type, setType] = useState("DELIVERY");
  const [projectId, setProjectId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [fields, setFields] = useState([]);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [batch, setBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = () => api.get("/imports").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/imports/fields").then((r) => setFields(r.data)).catch(() => {});
    loadHistory();
  }, []);

  const onFile = async (file) => {
    if (!projectId || !sourceId) {
      toast.error("Seleziona commessa e sorgente prima di caricare il file");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/imports/preview`, fd, { params: { service_type: type, project_id: projectId, source_id: sourceId } });
      setPreview(data);
      setMapping(data.mapping);
      if (data.incomplete_numbers) toast.warning(`${data.incomplete_numbers} codici lunghi (es. ID Risorsa) sono salvati nel file come numero e hanno perso le ultime cifre: verranno importati con le cifre disponibili e il segno «…». Per averli completi, nel file formatta quelle colonne come Testo.`, { duration: 15000 });
      setStep(1);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      const { data } = await api.post("/imports/commit", { staging_id: preview.staging_id, project_id: projectId, source_id: sourceId, service_type: type, mapping: clean });
      setBatch(data);
      setStep(2);
      toast.success(`Importazione completata: ${data.counts.nuovo} nuovi ordini`);
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const restart = () => { setStep(0); setPreview(null); setBatch(null); setMapping({}); };

  return (
    <div data-testid="import-page">
      <PageHeader eyebrow="Schermata B · Integration" title="Importazione ordini" />
      <Tabs defaultValue="manual">
        <TabsList className="mb-4">
          <TabsTrigger value="manual" data-testid="import-tab-manual">Import manuale</TabsTrigger>
          {can("group:manage") && <TabsTrigger value="auto" data-testid="import-tab-auto">Import automatico (API ed email)</TabsTrigger>}
        </TabsList>
        <TabsContent value="manual">
      <Stepper step={step} />
      {step === 0 && <SourceStep {...{ type, setType, projectId, setProjectId, sourceId, setSourceId, onFile, busy }} />}
      {step === 1 && preview && <MappingStep {...{ preview, fields, type, mapping, setMapping, busy }} onBack={() => setStep(0)} onCommit={commit} />}
      {step === 2 && batch && <ReportStep batch={batch} onRestart={restart} onPlan={can("planning:write") ? () => setStep(3) : null} />}
      {step === 3 && batch && <PlanStep batch={batch} onDone={restart} />}
        </TabsContent>
        <TabsContent value="auto"><ApiSection /><EmailSection /></TabsContent>
      </Tabs>
      <History items={history} />
    </div>
  );
}
